import api_client from '@/repositories';
import { IListOrganizationsResponse } from '@/types/apiClient/IListOrganizationsResponse';
import { UseMutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useListOrganizations() {
	return useQuery({
		queryKey: ['organizations', 'list'],
		queryFn: api_client.organizations.list,
	});
}

export function useGetOrganization(args: { id: string }) {
	return useQuery({
		queryKey: ['organizations', 'get', args],
		async queryFn() {
			const orgs = await api_client.organizations.list();
			const org = orgs.organizations.find((org) => org.id === args.id);
			return org;
		},
	});
}

interface ICreateOrganizationArgs
	extends UseMutationOptions<
		IListOrganizationsResponse,
		unknown,
		Parameters<typeof api_client.organizations.create>[0]
	> {}

export function useCreateOrganization(args?: ICreateOrganizationArgs) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api_client.organizations.create,
		onSuccess: (data, variables, context) => {
			if (args?.onSuccess) {
				args.onSuccess(data, variables, context);
			}
			queryClient.invalidateQueries({ queryKey: ['organizations', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', variables.id] });
		},
	});
}

export function useInviteMemberMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api_client.organizations.inviteMember,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['organizations', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', variables.organizationId] });
			queryClient.invalidateQueries({ queryKey: ['organizations', 'list-members', variables.organizationId] });
		},
	});
}

export function useListOrganizationMembers(id: string) {
	return useQuery({
		queryKey: ['organizations', 'list-members', id],
		queryFn: () => api_client.organizations.listMembers(id),
	});
}

export function useRemoveMemberMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api_client.organizations.removeMember,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['organizations', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', variables.orgId] });
			queryClient.invalidateQueries({ queryKey: ['organizations', 'list-members', variables.orgId] });
		},
	});
}

export function useAcceptOrgInviteMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api_client.organizations.acceptInvitation,
		onSuccess: () => {
			// We don't know the org so we need to invalidate all
			queryClient.invalidateQueries({ queryKey: ['organizations'] });
		},
	});
}

export function useGetPublicOrgInvitationQuery(code: string) {
	return useQuery({
		queryKey: ['organizations', 'public-invitation', code],
		queryFn: () => api_client.organizations.getPublicInvitation(code),
		retry: false,
	});
}

export function useDeleteOrganizationMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api_client.organizations.deleteOrganization,
		onSuccess: (_, id) => {
			queryClient.invalidateQueries({ queryKey: ['organizations', 'list'] });
			queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', id] });
		},
	});
}
